import { SiteSettings } from '@customTypes/siteSettings'
import {
  CalloutBannerFragment,
  FooterFragment,
  GlobalLabelsFragment,
  HeaderV2Fragment,
  RichSearchSuggestionFragment,
  SearchSuggestionFragment,
} from '@lib/umbraco/generated/types.generated'

import { getCulture } from '@lib/getCulture'
import { replaceInvalidLinks } from '@lib/replaceInvalidLinks'
import { replaceTypename } from '@lib/replaceTypename'
import { getGlobalData } from '@lib/umbraco'
import { getCountryConfig, getCurrencyFromCountry } from '@lib/utils'
import { PayloadAction } from '@reduxjs/toolkit'
import { CHANNEL, COUNTRIES, Country, REGIONS, Region } from 'config'
import { HYDRATE } from 'next-redux-wrapper'
import { AppContext } from 'next/app'
import { createAppSlice } from './createAppSlice'
import { AppThunk } from './store'

/**
 * Global state
 */
interface GlobalData {
  channel: CHANNEL
  pageUrl: string
  route: string
  siteSettings: SiteSettings
  regions: Region[]
  countries: Country[]
  retailerUrl: string
  wishlistUrl?: string
  header: HeaderV2Fragment
  calloutBanner: CalloutBannerFragment
  footer: FooterFragment
  labels: GlobalLabelsFragment
  searchSuggestions: SearchSuggestionFragment[]
  richSearchSuggestions: RichSearchSuggestionFragment[]
  productScrollTimeout: number
}

interface GlobalState {
  data: GlobalData | null
}
const initialGlobalState: GlobalState = {
  data: null,
}
export const globalSlice = createAppSlice({
  name: 'global',
  initialState: initialGlobalState,
  reducers: {
    globalLoaded(state, { payload }: PayloadAction<GlobalState>) {
      state.data = payload.data
    },
    setProductScrollTimeout(state, { payload }: PayloadAction<number>) {
      state.data.productScrollTimeout = payload
    },
  },
  selectors: {
    selectHeaderData: (state) => state.data?.header,
    selectFooterData: (state) => state.data?.footer,
    selectEcommerceEnabled: (state) =>
      state.data?.siteSettings.ecommerceEnabled,
    selectWishlistEnabled: (state) => state.data?.siteSettings.wishlistEnabled,
    selectAlpha2Code: (state) => state.data?.siteSettings.country?.code,
    selectRegions: (state) => state.data?.regions,
    selectCountries: (state) => state.data?.countries,
    selectCountry: (state) => state.data?.siteSettings.country,
    selectRegion: (state) => state.data?.siteSettings.region,
    selectCurrency: (state) =>
      getCurrencyFromCountry(state.data?.siteSettings.country?.code),
    selectFooter: (state) => state.data?.footer,
    selectCalloutBanner: (state) => state.data?.calloutBanner,
    selectRetailerUrl: (state) => state.data?.retailerUrl,
    selectWishlistUrl: (state) => state.data?.wishlistUrl,
    selectSearchSuggestions: (state) => state.data?.searchSuggestions,
    selectRichSearchSuggestions: (state) => state.data?.richSearchSuggestions,
    selectProductScrollTimeout: (state) => state.data?.productScrollTimeout,
    selectLabels: (state) => state.data?.labels,
    selectChannel: (state) => state.data.channel,
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      return {
        ...state,
        //@ts-ignore
        ...action.payload.global,
      }
    })
  },
})

export const fetchGlobalData =
  (payload: AppContext): AppThunk =>
  async (dispatch) => {
    const culture = getCulture(payload.router.query.country as string)

    const globalCmsData = await getGlobalData(culture)

    const countryConfig = getCountryConfig(
      payload.router.query.country as string,
    )

    const country = COUNTRIES.find(
      (country) => country.code.toLowerCase() == payload.router.query.country,
    )
    const region = REGIONS.find((r) =>
      r.countries.find((c) => c.code == country?.code),
    )

    let headerV2 = structuredClone(globalCmsData?.headerV2) ?? undefined
    let footer = structuredClone(globalCmsData?.footer) ?? undefined
    let calloutBanner =
      structuredClone(globalCmsData?.calloutBanner) ?? undefined
    replaceInvalidLinks(headerV2, globalCmsData.allContent as any)
    replaceInvalidLinks(footer, globalCmsData.allContent as any)
    replaceInvalidLinks(calloutBanner, globalCmsData.allContent as any)

    dispatch(
      globalSlice.actions.globalLoaded({
        data: replaceTypename({
          channel: countryConfig?.channel,
          pageUrl: payload.router.asPath,
          route: payload.router.route,
          siteSettings: {
            ecommerceEnabled: country?.ecommerceEnabled,
            country: country,
            region: region,
          },
          countries: COUNTRIES,
          regions: REGIONS,
          retailerUrl: globalCmsData.retailers?.url,
          wishlistUrl: globalCmsData.wishlist?.url,
          header: headerV2,
          footer,
          labels: globalCmsData.globalLables,
          calloutBanner: calloutBanner,
          searchSuggestions: globalCmsData.search?.suggestions,
          richSearchSuggestions: globalCmsData.search?.richSuggestions,
        }),
      }),
    )
  }

export const setProductScrollTimeout =
  (timeout: number): AppThunk =>
  async (dispatch) => {
    dispatch(globalSlice.actions.setProductScrollTimeout(timeout))
  }

// const globalSliceSelector = (state: AppState): GlobalState => state.global
// const selectGlobalData = createSelector(globalSliceSelector, (s) => s.data)
export const {
  selectHeaderData,
  selectFooterData,
  selectEcommerceEnabled,
  selectWishlistEnabled,
  selectAlpha2Code,
  selectRegions,
  selectCountries,
  selectCountry,
  selectCurrency,
  selectCalloutBanner,
  selectRegion,
  selectFooter,
  selectRetailerUrl,
  selectWishlistUrl,
  selectSearchSuggestions,
  selectRichSearchSuggestions,
  selectLabels,
  selectChannel,
  selectProductScrollTimeout,
} = globalSlice.selectors

export default globalSlice
